import { LitElement, html, css, nothing } from 'lit';
import type { PropertyValues } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';

export type AvatarGroupSize = 'xs' | 'sm' | 'md';

/**
 * Arranges a set of `asc-avatar` elements into an overlapping horizontal stack.
 *
 * Handles size propagation to all child avatars, suppresses their name/email
 * info blocks so only the circular frame is visible in the stack, and optionally
 * shows a `+N` overflow indicator when the number of children exceeds `max`.
 * An opt-in circular "Add" trigger can be rendered at the trailing end of the
 * stack, which fires `asc-group-add` when clicked.
 *
 * @element asc-avatar-group
 *
 * @fires asc-group-add - Fired when the add trigger is clicked. No detail.
 *
 * @slot          - One or more `asc-avatar` elements displayed in the stack. Required.
 * @slot add-button - Optional custom trigger rendered inside the add-trigger wrapper,
 *   replacing the default `+` icon. Only visible when `show-add` is `true`.
 *
 * @csspart base         - Root flex container holding the list and the add trigger.
 * @csspart list         - Inner flex container holding the slotted avatars and overflow badge.
 * @csspart overflow     - The `+N` circular overflow indicator.
 * @csspart add-trigger  - The default circular add button.
 *
 * @cssprop --asc-avatar-group-separator       - Ring color separating stacked avatars. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-avatar-group-gap             - Gap between the avatar stack and the add trigger. Default: `var(--asc-spacing-md)`
 * @cssprop --asc-avatar-group-overflow-bg     - Overflow badge background. Default: `var(--asc-bg-tertiary)`
 * @cssprop --asc-avatar-group-overflow-fg     - Overflow badge text color. Default: `var(--asc-text-quaternary)`
 * @cssprop --asc-avatar-group-overflow-border - Overflow badge border color. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-avatar-group-add-bg          - Add trigger background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-avatar-group-add-fg          - Add trigger icon color. Default: `var(--asc-border-primary)`
 * @cssprop --asc-avatar-group-add-border      - Add trigger border color. Default: `var(--asc-border-primary)`
 * @cssprop --asc-avatar-group-add-bg-hover    - Add trigger background on hover. Default: `var(--asc-bg-primary_hover)`
 */
@customElement('asc-avatar-group')
export class AscAvatarGroup extends LitElement {
  static override styles = css`
    :host {
      display: inline-flex;

      /*
       * Internal sizing vars — driven by [size] attribute.
       * xs is the default (matches the component default of size="sm" per spec,
       * but size="sm" below overrides to the sm values).
       */
      --_frame-size: 24px;
      --_overlap: var(--asc-spacing-xs, 4px);
      --_overflow-font-size: var(--asc-font-size-text-xs);
      --_overflow-lh: var(--asc-line-height-text-xs);
      --_icon-size: 16px;
    }

    :host([size='xs']) {
      --_frame-size: 24px;
      --_overlap: var(--asc-spacing-xs, 4px);
      --_overflow-font-size: var(--asc-font-size-text-xs);
      --_overflow-lh: var(--asc-line-height-text-xs);
      --_icon-size: 16px;
    }

    :host([size='sm']) {
      --_frame-size: 32px;
      --_overlap: var(--asc-spacing-md, 8px);
      --_overflow-font-size: var(--asc-font-size-text-sm);
      --_overflow-lh: var(--asc-line-height-text-sm);
      --_icon-size: 16px;
    }

    :host([size='md']) {
      --_frame-size: 40px;
      --_overlap: var(--asc-spacing-lg, 12px);
      --_overflow-font-size: var(--asc-font-size-text-md);
      --_overflow-lh: var(--asc-line-height-text-md);
      --_icon-size: 20px;
    }

    /* ── Root layout ──────────────────────────────────────────── */

    [part='base'] {
      display: inline-flex;
      align-items: center;
      gap: var(--asc-avatar-group-gap, var(--asc-spacing-md, 8px));
    }

    /* ── Avatar list ──────────────────────────────────────────── */

    [part='list'] {
      display: inline-flex;
      align-items: center;
    }

    /*
     * Stacking: margin-inline-start is applied via JS in _syncChildren() because
     * private shadow custom properties (--_overlap) are not reliably resolved
     * when referenced from ::slotted() rules — the value resolves at the slotted
     * element (light DOM), where the shadow-scoped private var is not inherited.
     *
     * CSS custom properties set here DO cascade into the child's own shadow root,
     * which is how --asc-avatar-info-display and --asc-avatar-ring-shadow work:
     * they are read by asc-avatar's own internal styles.
     */
    ::slotted(asc-avatar) {
      position: relative;

      /* Suppress the name/email info block inside asc-avatar's shadow root */
      --asc-avatar-info-display: none;

      /*
       * Ring shadow applied on asc-avatar's internal .frame (circular element)
       * via the --asc-avatar-ring-shadow hook, so it follows the frame's
       * border-radius rather than the rectangular host box.
       * 1.5px is a one-off decorative value not bound to a Figma spacing variable.
       */
      --asc-avatar-ring-shadow: 0 0 0 1.5px var(--asc-avatar-group-separator, var(--asc-bg-primary)); /* no-token: 1.5px separator ring */
    }

    /* ── Overflow badge ───────────────────────────────────────── */

    [part='overflow'] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      width: var(--_frame-size);
      height: var(--_frame-size);

      margin-inline-start: calc(-1 * var(--_overlap));
      box-shadow: 0 0 0 1.5px var(--asc-avatar-group-separator, var(--asc-bg-primary)); /* no-token: 1.5px ring */
      position: relative;

      background-color: var(--asc-avatar-group-overflow-bg, var(--asc-bg-tertiary));
      border-width: var(--asc-border-width, 1px);
      border-style: solid;
      border-color: var(--asc-avatar-group-overflow-border, var(--asc-border-secondary));
      border-radius: var(--asc-radius-full);

      color: var(--asc-avatar-group-overflow-fg, var(--asc-text-quaternary));
      font-family: var(--asc-font-family-body);
      font-size: var(--_overflow-font-size);
      line-height: var(--_overflow-lh);
      font-weight: var(--asc-font-weight-semibold);
      white-space: nowrap;
    }

    /* ── Add trigger ──────────────────────────────────────────── */

    [part='add-trigger'] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      cursor: pointer;

      width: var(--_frame-size);
      height: var(--_frame-size);

      background-color: var(--asc-avatar-group-add-bg, var(--asc-bg-primary));
      border-width: var(--asc-border-width, 1px);
      border-style: dashed;
      border-color: var(--asc-avatar-group-add-border, var(--asc-border-primary));
      border-radius: var(--asc-radius-full);

      color: var(--asc-avatar-group-add-fg, var(--asc-border-primary));

      padding: 0;
      margin: 0;

      transition: background-color 150ms ease;
    }

    [part='add-trigger']:hover {
      background-color: var(--asc-avatar-group-add-bg-hover, var(--asc-bg-primary_hover));
    }

    [part='add-trigger']:focus-visible {
      outline: var(--asc-focus-ring-width, 2px) solid var(--asc-focus-ring);
      outline-offset: var(--asc-focus-ring-offset, 2px);
    }

    [part='add-trigger']:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    /* ── Add icon (default plus glyph) ───────────────────────── */

    .add-icon {
      width: var(--_icon-size);
      height: var(--_icon-size);
      pointer-events: none;
      flex-shrink: 0;
    }
  `;

  /** Size propagated to all child asc-avatar elements. */
  @property({ type: String, reflect: true }) size: AvatarGroupSize = 'sm';

  /** When true, renders the circular add trigger at the trailing end of the stack. */
  @property({ type: Boolean, reflect: true, attribute: 'show-add' }) showAdd = false;

  /** Maximum number of child avatars to display. Surplus avatars are hidden and a +N indicator is shown. */
  @property({ type: Number }) max: number | null = null;

  @state() private _overflowCount = 0;
  @state() private _hasAddSlot = false;

  @query('slot:not([name])') private _defaultSlot!: HTMLSlotElement;

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('size') || changed.has('max')) {
      this._syncChildren();
    }
  }

  private _handleSlotChange(): void {
    this._syncChildren();
  }

  private _handleAddSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasAddSlot = slot.assignedElements().length > 0;
  }

  private _syncChildren(): void {
    const assigned = (this._defaultSlot?.assignedElements() ?? [])
      .filter((el): el is HTMLElement => el.tagName === 'ASC-AVATAR');

    /*
     * Overlap amounts match the Figma design per size step.
     * Applied as inline styles (not via ::slotted CSS) so the values are
     * resolved in JS rather than relying on shadow-scoped private CSS
     * variables being inherited by slotted light-DOM elements.
     */
    const overlapPx: Record<AvatarGroupSize, number> = { xs: 4, sm: 8, md: 12 };
    const overlap = overlapPx[this.size] ?? 8;

    assigned.forEach((el, i) => {
      el.setAttribute('size', this.size);
      // First avatar has no start offset; subsequent avatars pull left to overlap.
      el.style.marginInlineStart = i === 0 ? '0' : `-${overlap}px`;
      // Apply truncation when max is set
      el.hidden = this.max !== null && i >= this.max;
    });

    this._overflowCount =
      this.max !== null ? Math.max(0, assigned.length - this.max) : 0;
  }

  private _handleAddClick(): void {
    dispatch(this, 'asc-group-add');
  }

  override render() {
    return html`
      <div part="base" role="group" aria-label="Members">
        <div part="list">
          <slot @slotchange=${this._handleSlotChange}></slot>

          ${this._overflowCount > 0
            ? html`<span
                part="overflow"
                role="img"
                aria-label="+${this._overflowCount} more members"
              >+${this._overflowCount}</span>`
            : nothing}
        </div>

        ${this.showAdd
          ? html`<button
              part="add-trigger"
              type="button"
              aria-label="Add member"
              @click=${this._handleAddClick}
            >
              <slot
                name="add-button"
                @slotchange=${this._handleAddSlotChange}
              ></slot>
              ${!this._hasAddSlot
                ? html`<svg
                    class="add-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 5v14M5 12h14"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>`
                : nothing}
            </button>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-avatar-group': AscAvatarGroup;
  }
}
